import classNames from "classnames";
import React, { createContext, useContext } from "react";

/**
 * - `card`: the bordered settings card from the reworked interaction previews
 *   (Design Studio `bubbles-current`), rows split by hairlines.
 * - `open`: no card and no rules, evenly spaced rows straight on the popup sheet
 *   (Design Studio `actor-settings/redesign-attach-full-v3`).
 */
export type PiSettingsListVariant = "card" | "open";

const PiSettingsListVariantContext = createContext<PiSettingsListVariant>("card");

/**
 * One row per setting with a fixed-width label and a control. Groups are split by
 * a hairline, not headed: the row labels already say what a group is about.
 *
 *   <PiSettingsList>
 *     <PiSettingsRow label="Speaker"><PiActorChipSelect … /></PiSettingsRow>
 *     <PiSettingsGap />
 *     <PiSettingsRow label="Animation" below={…} />
 *   </PiSettingsList>
 */
export function PiSettingsList(props: {
  children: React.ReactNode;
  className?: string;
  variant?: PiSettingsListVariant;
}) {
  const variant = props.variant ?? "card";
  return (
    <PiSettingsListVariantContext.Provider value={variant}>
      <div
        className={classNames(
          "flex flex-col self-start",
          {
            "rounded-lg border border-solid border-interaction-primary-washed bg-creator-surface":
              variant === "card",
          },
          props.className
        )}
      >
        {props.children}
      </div>
    </PiSettingsListVariantContext.Provider>
  );
}

/** Break between two groups of rows: a hairline one step darker than the row rules. */
export function PiSettingsGap() {
  return <div className="h-px flex-none bg-creator-border" role="separator" />;
}

export function PiSettingsRow(props: {
  label: string;
  /** The control beside the label. Leave out for a label-only row with `below`. */
  children?: React.ReactNode;
  /** Shown full-width under the label and control, e.g. a preview or a hint. */
  below?: React.ReactNode;
  disabled?: boolean;
}) {
  const isOpen = useContext(PiSettingsListVariantContext) === "open";
  const line = (
    <div
      className={classNames("flex items-center gap-3", {
        "min-h-[56px]": props.children && !isOpen,
        "min-h-[52px]": props.children && isOpen,
        "pt-3 pb-2.5": !props.children,
      })}
    >
      <span
        className={classNames("w-[128px] flex-none text-[13px]", {
          "text-creator-text-sub": !props.disabled,
          "text-creator-text-faded": props.disabled,
        })}
      >
        {props.label}
      </span>
      {props.children}
    </div>
  );

  return (
    <div
      className={classNames({
        "px-4 border-b border-solid border-x-0 border-t-0 border-interaction-primary-washed last:border-b-0":
          !isOpen,
      })}
    >
      {line}
      {props.below && <div className="pb-3">{props.below}</div>}
    </div>
  );
}
